import { AuthUserQuery } from 'dashboard-services/api/auth';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { globalActions, loginActions } from 'primary-components';

import { creatorCurvesActions } from 'actions/curves';
import { basicDatalakeActions } from 'actions/datalake';
import { creatorSeriesActions } from 'actions/series';
import { basicUploadActions } from 'actions/upload';
import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';
import { CurvesUtils, ExcelUtils, FileUtils, GAUtils, PropertiesUtils, TimeSeriesUtils } from 'utils';

const FUNCTIONS_NAMESPACE = properties.functionsNamespace

if (module.hot) {
  module.hot.accept();
} 

const RefreshCommands = (() => {

  const refreshCell = async e => {
    try {
      GAUtils.sendEvent({
        category: "view_item",
        action: "RefreshCell",
        label: "command"
      })
      await Excel.run(async context => {
        const cell = context.workbook.getActiveCell();
        cell.calculate();
        await context.sync();
      });
    } catch (error) {
      console.error(error);
    } finally {
      e.completed();
    }
  }

  const refreshSheet = async e => {
    try {
      GAUtils.sendEvent({
        category: "view_item",
        action: "RefreshSheet",
        label: "command"
      })
      await Excel.run(async context => {
        const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
        await ExcelUtils.refreshAllFormulasInSheet({ worksheet: activeWorksheet, context })
      });
    } catch (error) {
      console.error(error);
    } finally {
      e.completed();
    }
  }

  const refreshAll = async e => {
    try {
      GAUtils.sendEvent({
        category: "view_item",
        action: "RefreshAll",
        label: "command"
      })
      await Excel.run(async context => {
        await ExcelUtils.refreshAllFormulas({ context })
      });
    } catch (error) {
      console.error(error);
    } finally {
      e.completed();
    }
  }

  return {
    refreshCell,
    refreshSheet,
    refreshAll
  }
})()

const AuthCommands = (() => {

  const logOut = async e => {
    try {
      const getQueryBuilder = () => new Query.Builder(window["ngStore" + FUNCTIONS_NAMESPACE].dispatch(getApiConfig()))
              .withShouldDisplayError(false),
            logOutCall = AuthUserQuery
              .QUERIES
              .logOutMutation(getQueryBuilder)
              .mutationFn
      window["ngStore" + FUNCTIONS_NAMESPACE].dispatch(loginActions.logOut({ logOutCall, withSideEffects: false }))
    } catch (error) {
      console.error(error)
    } finally {
      e.completed()
    }
  }

  return {
    logOut
  }
})()


const NavigateCommands = (() => {

  const openDocumentation = async e => {
    try {
      const baseUrl = PropertiesUtils.getBaseApiObjByAPi()
      CommonService.openInNewTab(`${baseUrl.dashboardUrl}${routing.EXTERNAL.documentation.url}`)
    } catch (error) {
      console.error(error)
    }
    e.completed()
  }

  const openDataLake = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.LAKE.name).finally(() => e.completed()) 
  }
  const openTimeSeries = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.TS.name).finally(() => e.completed()) 
  }
  const openForwardCurves = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.CURVES.name).finally(() => e.completed()) 
  }
  const openLogin = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.LOGIN.name).finally(() => e.completed()) 
  }
  const openSettings = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.SETTINGS.name).finally(() => e.completed()) 
  }
  const openUpload = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.UPLOAD.name).finally(() => e.completed()) 
  }
  const openActive = async e => {
    await ExcelUtils.openTaskPaneComponent(routing.ACTIVE.name).finally(() => e.completed()) 
  }

  return {
    openDocumentation,
    openDataLake,
    openTimeSeries,
    openForwardCurves,
    openLogin,
    openSettings,
    openUpload,
    openActive
  }
})()

const CommonCommands = (() => {
  const editFormula = async e => {
    try {
      GAUtils.sendEvent({
        category: "view_item",
        action: "editFormula",
        label: "command"
      })
      await Excel.run(async context => {
        const cell = context.workbook.getActiveCell();
        cell.load("address")
        cell.load("formulas")
        await context.sync()
        const formula = cell.formulas?.[0]?.[0],
              tsParsed = await TimeSeriesUtils.validateTable({
                cell,
                formula,
                returnItem: true
              }),
              curveParsed = await CurvesUtils.validateTable({
                cell,
                formula,
                returnItem: true
              }),
              fileParsed = FileUtils.validateTable({
                cell,
                formula,
              })
        if(tsParsed) {
          const dispatch =  window["ngStore" + FUNCTIONS_NAMESPACE].dispatch,
                groupedParams = tsParsed.parsedParameters.getGroupedParams()
          await dispatch(creatorSeriesActions.editFormula({ 
            address: tsParsed.address, 
            keys: groupedParams.map(gp => ReduxService.updateObject(gp, { columns: gp.columns?.split(",") })),
            params: tsParsed.parsedParameters
          }))
          await ExcelUtils.openTaskPaneComponent(routing.TS.name)
          } else if(curveParsed) {
            const dispatch =  window["ngStore" + FUNCTIONS_NAMESPACE].dispatch,
                  groupedParams = curveParsed.parsedParameters.getGroupedParams()
            await dispatch(creatorCurvesActions.editFormula({ 
              address: curveParsed.address, 
              keys: groupedParams,
              params: curveParsed.parsedParameters
            }))
            await ExcelUtils.openTaskPaneComponent(routing.CURVES.name)
        } else if(fileParsed?.length > 0) {
          const dispatch =  window["ngStore" + FUNCTIONS_NAMESPACE].dispatch
          await dispatch(basicDatalakeActions.editFormula(fileParsed?.[0]))
          await ExcelUtils.openTaskPaneComponent(routing.LAKE.name)
        } else {
          console.warn("Not our formula, not opening any task pane", formula)
        }
        await context.sync()
      });
    } catch (error) {
      console.error(error)
    }
    e.completed()
  }

  return {
    editFormula
  }
})()

const UploadCommands = (() => {

  const selectRange = async e => {
    try {
      GAUtils.sendEvent({
        category: "view_item",
        action: "UploadCommands.selectRange",
        label: "command"
      })
      await ExcelUtils.openTaskPaneComponent(routing.UPLOAD.name)
      await window["ngStore" + FUNCTIONS_NAMESPACE].dispatch(basicUploadActions.defineFileRange())
    } catch (error) {
      console.error(error)
    }
    e.completed()
  }

  return {
    selectRange
  }
})()

const TableCommands = (() => {

  const clearTable = async e => {
    try {
      GAUtils.sendEvent({
        category: "view_item",
        action: "TableCommands.clearTable",
        label: "command"
      })

      ExcelUtils.clearActiveRangeTable()
    } catch (error) {
      window["ngStore" + FUNCTIONS_NAMESPACE].dispatch(globalActions.getMessageStore())?.error(error.message)
      console.error(error)
    }
    e.completed()
  }

  return {
    clearTable
  }
})()

Office.onReady(() => {
  Office.actions.associate("RefreshCommands.refreshCell", RefreshCommands.refreshCell);
  Office.actions.associate("RefreshCommands.refreshSheet", RefreshCommands.refreshSheet);
  Office.actions.associate("RefreshCommands.refreshAll", RefreshCommands.refreshAll);
  Office.actions.associate("AuthCommands.logOut", AuthCommands.logOut);
  Office.actions.associate("NavigateCommands.openDocumentation", NavigateCommands.openDocumentation);
  Office.actions.associate("NavigateCommands.openDataLake", NavigateCommands.openDataLake);
  Office.actions.associate("NavigateCommands.openTimeSeries", NavigateCommands.openTimeSeries);
  Office.actions.associate("NavigateCommands.openForwardCurves", NavigateCommands.openForwardCurves);
  Office.actions.associate("NavigateCommands.openLogin", NavigateCommands.openLogin);
  Office.actions.associate("NavigateCommands.openSettings", NavigateCommands.openSettings);
  Office.actions.associate("NavigateCommands.openUpload", NavigateCommands.openUpload);
  Office.actions.associate("NavigateCommands.openActive", NavigateCommands.openActive);
  Office.actions.associate("CommonCommands.editFormula", CommonCommands.editFormula);
  Office.actions.associate("UploadCommands.selectRange", UploadCommands.selectRange);
  Office.actions.associate("TableCommands.clearTable", TableCommands.clearTable);
})
